import { AuthFetchWithResponse } from 'src/Types/api'
import { RetrofitDto, RetrofitListItemDto } from './dto/retrofit.dto'
import { Retrofit, RetrofitListItem, RetrofitPartnerContact } from '../models/retrofit.model'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { retrofitDtoToModelTransformer, retrofitListItemDtoToModelTransformer } from './transformer/retrofit-dto-to-model.transformer'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { isFlowTheme } from 'src/Libs/utils'
import { isBefore } from '@e3dc-react/shell/Libs/date'

/**
 * Fetch the retrofit status for a specified system.
 * @param serialnumber - serialnumber of the system to be checked
 * @param authFetch
 * @returns - Retrofit Status
 */
export const fetchRetrofitById = async (serialnumber: string | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<Retrofit>> => {
  if (isFlowTheme) return new ApiResult<Retrofit>(null, false) //TODO remove when Flow supports this feature
  if (!serialnumber) {
    return new ApiResult<Retrofit>(null, true, new Error('Serialnumber not provided'))
  }

  try {
    const response = await authFetch<RetrofitDto>({
      path: `retrofit/${serialnumber}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_RETROFIT_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      const model = retrofitDtoToModelTransformer(response.data)
      return new ApiResult<Retrofit>(model, false)
    }
    return new ApiResult<Retrofit>(null, true)
  } catch (error) {
    return handleFetchError<Retrofit>(error)
  }
}

/**
 * Fetch the retrofit list for an installation.
 *
 * @param installationId
 * @param authFetch
 * @returns - List of systems inside the installation that qualify for a retrofit upgrade
 */
export const fetchRetrofitList = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<RetrofitListItem[]>> => {
  if (!installationId) {
    return new ApiResult<RetrofitListItem[]>(null, true, new Error('InstallationId not provided'))
  }

  try {
    const response = await authFetch<RetrofitListItemDto[]>({
      path: `retrofit/list/${installationId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_RETROFIT_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      const model = response.data.map(item => retrofitListItemDtoToModelTransformer(item))
      return new ApiResult<RetrofitListItem[]>(model, false)
    }
    return new ApiResult<RetrofitListItem[]>(null, true)
  } catch (error) {
    return handleFetchError<RetrofitListItem[]>(error)
  }
}

/**
 * Fetch the partners list for a retrofit upgrade.
 *
 * @param installationId
 * @param authFetch
 * @returns - List of partners that can be contacted for a retrofit upgrade
 */
export const fetchRetrofitPartnersList = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<RetrofitPartnerContact[]>> => {
  if (!installationId) {
    return new ApiResult<RetrofitPartnerContact[]>(null, true, new Error('InstallationId not provided'))
  }

  try {
    const response = await authFetch<RetrofitPartnerContact[]>({
      path: `retrofit/list/${installationId}/partner-contacts`,
      method: 'GET',
      endpoint: process.env.REACT_APP_RETROFIT_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<RetrofitPartnerContact[]>(response.data, false)
    }
    return new ApiResult<RetrofitPartnerContact[]>(null, true)
  } catch (error) {
    return handleFetchError<RetrofitPartnerContact[]>(error)
  }
}

/**
 *
 * @param retrofitList - List of retrofit systems + expiration date
 * @returns  the item from that list that has the closest expiration date
 */
export const getClosestExpirationItem = (retrofitList: RetrofitListItem[]): RetrofitListItem | null => {
  if (retrofitList.length === 0) return null

  return retrofitList.reduce((prev, current) => (isBefore(new Date(current.expiresAt), new Date(prev.expiresAt)) ? current : prev), retrofitList[0])
}
